import React, {useMemo, useState} from "react";
import {Box, Button, type ButtonProps, Skeleton, Stack, Typography} from "@mui/material";
import {observer} from "mobx-react-lite";
import ArrowIcon from "../../../../components/icons/svg/ChevronDown.svg?react";
import {useAgentMetadata} from "../../hooks/useAgentMetadata";
import type {AgentMetadata} from "../../types/chatTypes";
import {isAgentEnabledForDatabag} from "../../utils/agentDatabag";
import {AgentLogoIcon} from "./agentLogo";
import {CustomPopper} from "../../../../components/customPopper/CustomPopper";
import LockIcon from "../../../../components/icons/svg/LockIcon.svg?react";
import {useStores} from "../../../../stores";


export type AgentSelectButtonProps = {
    /** Uncontrolled initial selection (`app_name`); defaults to the first row after `sort_order`. */
    defaultSelectedId?: string;
    /** Controlled selection (`app_name`). When set, the parent owns the selected agent. */
    selectedId?: string;
    onSelectionChange?: (agent: AgentMetadata) => void;
    position?: "center" | "start" | "end";
} & Omit<ButtonProps, "onClick" | "children" | "startIcon" | "endIcon">;

const AgentSelectButtonComponent: React.FC<AgentSelectButtonProps> = ({
                                                                          defaultSelectedId,
                                                                          selectedId: selectedIdProp,
                                                                          onSelectionChange,
                                                                          sx,
                                                                          disabled,
                                                                          position = "center",
                                                                          ...buttonProps
                                                                      }) => {
    const {bagStore} = useStores();
    const activeDatabagId = bagStore.bagId;
    const {data: agentsMetadata, isPending, isError} = useAgentMetadata();

    const sortedAgents = useMemo(() => {
        if (!agentsMetadata?.length) {
            return [];
        }
        return [...agentsMetadata].sort((a, b) => a.sort_order - b.sort_order);
    }, [agentsMetadata]);

    const isControlled = selectedIdProp !== undefined;
    const [internalAppName, setInternalAppName] = useState<string | null>(null);

    const selectedAgent = useMemo(() => {
        if (!sortedAgents.length) {
            return null;
        }
        const wanted = isControlled
            ? selectedIdProp
            : (internalAppName ?? defaultSelectedId);
        const match = wanted
            ? sortedAgents.find((a) => a.app_name === wanted)
            : null;
        if (match) {
            return match;
        }
        const atlas = sortedAgents.find((a) => a.app_name === "atlas");
        return atlas ?? sortedAgents[0];
    }, [
        sortedAgents,
        isControlled,
        selectedIdProp,
        internalAppName,
        defaultSelectedId,
    ]);

    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const open = Boolean(anchorEl);

    const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const commitSelection = (agent: AgentMetadata) => {
        if (!isControlled) {
            setInternalAppName(agent.app_name);
        }
        onSelectionChange?.(agent);
    };

    const handleSelectItem = (agent: AgentMetadata) => {
        commitSelection(agent);
        handleClose();
    };

    if (isPending) {
        return (
            <Skeleton
                variant="rounded"
                width={110}
                height={40}
                sx={{borderRadius: 1}}
            />
        );
    }

    if (isError || !sortedAgents.length || !selectedAgent) {
        return null;
    }
    return (
        <>
            <Stack alignItems="flex-start" spacing={1} sx={{maxWidth: "100%"}}>
                <Button
                    {...buttonProps}
                    disabled={disabled}
                    onClick={handleOpen}
                    variant="text"
                    size="small"
                    startIcon={
                        <Box
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "text.primary",
                                "& svg": {display: "block"},
                            }}
                        >
                            <AgentLogoIcon agent={selectedAgent}/>
                        </Box>
                    }
                    endIcon={
                        <Box
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                transition: "transform 0.2s ease",
                                transform: "rotate(180deg)",
                                "& svg": {display: "block"},
                            }}
                        >
                            <ArrowIcon/>
                        </Box>
                    }
                    sx={{
                        ...sx,
                        textTransform: "none",
                        fontWeight: 400,
                        fontSize: "1rem",
                        height: "32px",
                    }}
                >
                    {selectedAgent.display_name}
                </Button>
            </Stack>
            <CustomPopper
                open={open}
                keepMounted
                disableTransition
                onClose={handleClose}
                anchorEl={anchorEl}
                placement={position === "end" ? "top-start" : "auto"}
                paperSx={{px: 0, py: "8px"}}
            >
                <Box
                    component="ul"
                    sx={{listStyle: "none", m: 0, p: 0, width: "309px"}}
                >
                    {sortedAgents.map((agent) => {
                        const isSelected = agent.app_name === selectedAgent.app_name;
                        const agentEnabled = isAgentEnabledForDatabag(
                            agent,
                            activeDatabagId,
                        );

                        return (
                            <Stack
                                direction="row"
                                alignItems="center"
                                component="li"
                                key={agent.app_name}
                                {...(agentEnabled && {
                                    onClick: () => handleSelectItem(agent),
                                })}
                                sx={{
                                    height: "56px",
                                    gap: 1.5,
                                    px: 2,
                                    py: 0.5,
                                    cursor: agentEnabled ? "pointer" : "default",
                                    bgcolor: isSelected ? "action.selected" : "transparent",
                                    "&:hover": {
                                        bgcolor: isSelected ? "action.selected" : "action.hover",
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        flexShrink: 0,
                                        display: "inline-flex",
                                        alignItems: "flex-start",
                                        pt: 0.25,
                                        "& svg": {display: "block"},
                                        opacity: agentEnabled ? 1 : 0.5,
                                    }}
                                >
                                    <AgentLogoIcon agent={agent}/>
                                </Box>
                                <Stack justifyContent={"space-around"} sx={{height: "100%"}}>
                                    <Typography
                                        variant="body2"
                                        component="div"
                                        sx={{opacity: agentEnabled ? 1 : 0.5}}
                                    >
                                        {agent.display_name}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{mt: 0.25, color: "#44474e"}}
                                    >
                                        {agentEnabled
                                            ? agent.description
                                            : "Upgrade to access this agent"}
                                    </Typography>
                                </Stack>
                                {!agentEnabled && <LockIcon style={{marginLeft: "auto"}}/>}
                            </Stack>
                        );
                    })}
                </Box>
            </CustomPopper>
        </>
    );
};

export const AgentSelectButton = observer(AgentSelectButtonComponent);